import { useCallback, useEffect, useMemo, useState } from "react";
import { Braces, Clock3, Eye, GitCompareArrows, History, RotateCcw, Sparkles, Star, User2 } from "lucide-react";
import { Skeleton, SkeletonCard } from "@/components/skeleton";
import {
  publishPage,
  restorePageVersion,
  usePageVersions,
  type PageVersionSummary,
} from "@/hooks/use-pages";
import { createVersionComparison, listChangedPropKeys } from "./version-history-comparison";

interface VersionHistoryPanelProps {
  readonly pageId: string | undefined;
  readonly currentVersionId?: string | null;
  readonly publishedVersionId?: string | null;
  readonly onRestore?: () => Promise<void> | void;
  readonly onPreview?: (version: PageVersionSummary) => void;
}

const MAX_TIMELINE_DOTS = 50;
type DiffViewMode = "visual" | "data";

function formatTimestamp(iso: string): string {
  const date = new Date(iso);
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatRelativeAge(iso: string): string {
  const deltaMs = Date.now() - new Date(iso).getTime();
  const deltaMinutes = Math.max(1, Math.round(deltaMs / 60_000));

  if (deltaMinutes < 60) {
    return `${deltaMinutes}m ago`;
  }

  const deltaHours = Math.round(deltaMinutes / 60);
  if (deltaHours < 24) {
    return `${deltaHours}h ago`;
  }

  const deltaDays = Math.round(deltaHours / 24);
  return `${deltaDays}d ago`;
}

function countBlocks(puckData: unknown): number {
  if (!puckData || typeof puckData !== "object") {
    return 0;
  }

  const data = puckData as {
    content?: unknown[];
    zones?: Record<string, unknown[]>;
  };

  const topLevel = Array.isArray(data.content) ? data.content.length : 0;
  const nested = Object.values(data.zones ?? {}).reduce((count, zoneItems) => {
    return count + (Array.isArray(zoneItems) ? zoneItems.length : 0);
  }, 0);

  return topLevel + nested;
}

function readLabel(version: PageVersionSummary): string {
  if (version.generationSource === "ai") {
    return version.aiPrompt ? "AI generated" : "AI revision";
  }

  if (version.generationSource === "duplicate") {
    return "Duplicated baseline";
  }

  if (version.generationSource === "autosave") {
    return "Autosaved checkpoint";
  }

  return "Manual save";
}

function readDotTone(version: PageVersionSummary, isCurrent: boolean, isPublished: boolean): string {
  if (isPublished) {
    return "border-amber-400 bg-amber-400 shadow-[0_0_0_4px_rgba(251,191,36,0.18)]";
  }

  if (isCurrent) {
    return "border-[var(--color-primary)] bg-[var(--color-primary)] shadow-[0_0_0_4px_color-mix(in_srgb,var(--color-primary)_18%,transparent)]";
  }

  if (version.generationSource === "ai") {
    return "border-[var(--color-primary)]/50 bg-[var(--color-primary)]/25";
  }

  if (version.generationSource === "duplicate") {
    return "border-amber-400/70 bg-amber-400/25";
  }

  return "border-border bg-[var(--color-surface)]";
}

function readDotSize(version: PageVersionSummary): string {
  return version.generationSource === "autosave" ? "h-3 w-3" : "h-4 w-4";
}

function buildTimelineTooltip(version: PageVersionSummary): string {
  return `Version ${version.versionNumber} • ${readLabel(version)} • ${formatTimestamp(version.createdAt)}`;
}

export function VersionHistoryPanel({
  pageId,
  currentVersionId,
  publishedVersionId,
  onRestore,
  onPreview,
}: VersionHistoryPanelProps) {
  const { versions, isLoading, error, refetch } = usePageVersions(pageId);
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [publishingRestoreId, setPublishingRestoreId] = useState<string | null>(null);
  const [isScrubbing, setIsScrubbing] = useState(false);
  const [hoveredVersionId, setHoveredVersionId] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [compareSelection, setCompareSelection] = useState<string[]>([]);
  const [diffView, setDiffView] = useState<DiffViewMode>("visual");

  const displayedVersions = useMemo(() => versions.slice(0, MAX_TIMELINE_DOTS), [versions]);
  const selectedVersion = useMemo(() => {
    if (displayedVersions.length === 0) {
      return null;
    }

    return displayedVersions.find((version) => version.id === selectedVersionId)
      ?? displayedVersions.find((version) => version.id === currentVersionId)
      ?? displayedVersions[0]
      ?? null;
  }, [currentVersionId, displayedVersions, selectedVersionId]);
  const selectedCompareVersions = useMemo(
    () => displayedVersions.filter((version) => compareSelection.includes(version.id)),
    [compareSelection, displayedVersions],
  );
  const hoveredVersion = useMemo(
    () => displayedVersions.find((version) => version.id === hoveredVersionId) ?? null,
    [displayedVersions, hoveredVersionId],
  );
  const hoverComparison = useMemo(
    () => (selectedVersion && hoveredVersion && selectedVersion.id !== hoveredVersion.id
      ? createVersionComparison(selectedVersion, hoveredVersion)
      : null),
    [hoveredVersion, selectedVersion],
  );
  const lastKnownGoodVersionId = publishedVersionId && publishedVersionId !== currentVersionId
    ? publishedVersionId
    : null;
  const comparison = useMemo(
    () => (selectedCompareVersions.length === 2
      ? createVersionComparison(selectedCompareVersions[0]!, selectedCompareVersions[1]!)
      : null),
    [selectedCompareVersions],
  );
  const changedIds = useMemo(
    () => new Set(comparison?.changed.map((entry) => entry.componentId) ?? []),
    [comparison],
  );
  const onlyInAIds = useMemo(
    () => new Set(comparison?.onlyInA.map((entry) => entry._id ?? entry.type) ?? []),
    [comparison],
  );
  const onlyInBIds = useMemo(
    () => new Set(comparison?.onlyInB.map((entry) => entry._id ?? entry.type) ?? []),
    [comparison],
  );

  const handleRestore = useCallback(async (version: PageVersionSummary) => {
    if (!pageId || restoringId || publishingRestoreId) {
      return;
    }

    setActionMessage(null);
    setRestoringId(version.id);
    try {
      await restorePageVersion(pageId, version.id);
      await refetch();
      await onRestore?.();
      setSelectedVersionId(version.id);
    } finally {
      setRestoringId(null);
    }
  }, [onRestore, pageId, publishingRestoreId, refetch, restoringId]);

  const handleRestoreAndPublish = useCallback(async (version: PageVersionSummary) => {
    if (!pageId || restoringId || publishingRestoreId) {
      return;
    }

    setActionMessage(null);
    setPublishingRestoreId(version.id);
    try {
      await restorePageVersion(pageId, version.id);
      await publishPage(pageId);
      await refetch();
      await onRestore?.();
      setSelectedVersionId(version.id);
      setActionMessage(`Rolled back to version from ${formatTimestamp(version.createdAt)}.`);
    } finally {
      setPublishingRestoreId(null);
    }
  }, [onRestore, pageId, publishingRestoreId, refetch, restoringId]);

  const toggleCompareSelection = useCallback((versionId: string) => {
    setCompareSelection((current) => {
      if (current.includes(versionId)) {
        return current.filter((candidate) => candidate !== versionId);
      }

      if (current.length === 2) {
        return [current[1]!, versionId];
      }

      return [...current, versionId];
    });
  }, []);

  useEffect(() => {
    if (!selectedVersion || !onPreview) {
      return;
    }
    onPreview(selectedVersion);
  }, [onPreview, selectedVersion]);

  useEffect(() => {
    if (!isScrubbing) {
      return;
    }

    const stopScrubbing = () => setIsScrubbing(false);
    window.addEventListener("mouseup", stopScrubbing);
    window.addEventListener("mouseleave", stopScrubbing);
    return () => {
      window.removeEventListener("mouseup", stopScrubbing);
      window.removeEventListener("mouseleave", stopScrubbing);
    };
  }, [isScrubbing]);

  if (!pageId) {
    return (
      <div className="flex h-full flex-col bg-card">
        <div className="border-b border-border px-5 py-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <History className="h-4 w-4" />
            Version History
          </div>
          <p className="mt-1 text-xs leading-5 text-muted-foreground">
            Save the page first to start a recoverable timeline.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col bg-card">
      <div className="border-b border-border px-5 py-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <History className="h-4 w-4" />
              Version History
            </div>
            <p className="mt-1 text-xs leading-5 text-muted-foreground">
              Scrub through saved states, preview the selected checkpoint, and restore non-destructively.
            </p>
          </div>
          <div className="rounded-full border border-border bg-[var(--color-surface)] px-2.5 py-1 text-[11px] font-medium text-muted-foreground">
            {versions.length} saved
          </div>
        </div>
      </div>

      <div className="border-b border-border px-5 py-4">
        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-4 w-32" />
            <SkeletonCard className="h-20" />
          </div>
        ) : versions.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border bg-[var(--color-surface)] px-4 py-5 text-sm text-muted-foreground">
            No saved versions yet. The timeline will appear after the first real save.
          </div>
        ) : (
          <div>
            <div className="mb-3 flex items-center justify-between gap-3 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              <span>Timeline</span>
              {versions.length > MAX_TIMELINE_DOTS ? (
                <span>Showing latest {MAX_TIMELINE_DOTS}</span>
              ) : null}
            </div>
            <div
              className="overflow-x-auto pb-3"
              role="tablist"
              aria-label="Page version timeline"
              onMouseLeave={() => setIsScrubbing(false)}
            >
              <div className="relative flex min-w-max items-center gap-3 px-1 py-4">
                <div className="absolute left-0 right-0 top-1/2 h-px -translate-y-1/2 bg-border" />
                {displayedVersions.map((version) => {
                  const isCurrent = version.id === currentVersionId;
                  const isPublished = version.id === publishedVersionId;
                  const isLastKnownGood = version.id === lastKnownGoodVersionId;
                  const isSelected = version.id === selectedVersion?.id;

                  return (
                    <button
                      key={version.id}
                      type="button"
                      role="tab"
                      aria-selected={isSelected}
                      aria-label={`Select version ${version.versionNumber}`}
                      title={buildTimelineTooltip(version)}
                      onMouseDown={() => {
                        setIsScrubbing(true);
                        setSelectedVersionId(version.id);
                      }}
                      onMouseEnter={() => {
                        setHoveredVersionId(version.id);
                        if (isScrubbing) {
                          setSelectedVersionId(version.id);
                        }
                      }}
                      onFocus={() => setSelectedVersionId(version.id)}
                      onClick={() => setSelectedVersionId(version.id)}
                      className="relative z-[1] flex min-w-[72px] flex-col items-center gap-2 rounded-2xl px-2 py-1 text-center transition hover:bg-accent"
                    >
                      <span className="relative">
                        <span
                          className={`${readDotSize(version)} block rounded-full border-2 transition ${readDotTone(version, isCurrent, isPublished)}`}
                        />
                        {isPublished ? (
                          <Star className="absolute -right-2 -top-2 h-3.5 w-3.5 fill-amber-400 text-amber-500" aria-hidden="true" />
                        ) : null}
                      </span>
                      <span className={`text-xs font-medium ${isSelected ? "text-foreground" : "text-muted-foreground"}`}>
                        v{version.versionNumber}
                      </span>
                      {isLastKnownGood ? (
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.12em] text-amber-800">
                          last good
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </div>
            {hoverComparison ? (
              <div className="mt-2 rounded-2xl border border-border bg-card px-3 py-2 text-xs text-muted-foreground shadow-sm">
                <div className="flex items-center gap-2 font-semibold text-foreground">
                  <GitCompareArrows className="h-3.5 w-3.5" />
                  Hover diff preview: v{hoverComparison.versionA.versionNumber} → v{hoverComparison.versionB.versionNumber}
                </div>
                <div className="mt-1">
                  {hoverComparison.changed.length} changed, {hoverComparison.onlyInB.length} added, {hoverComparison.onlyInA.length} removed blocks.
                </div>
              </div>
            ) : null}
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4">
        {error ? (
          <div className="rounded-2xl border border-destructive/25 bg-destructive/5 px-4 py-3 text-sm text-destructive">
            {error}
          </div>
        ) : null}

        {!isLoading && selectedVersion ? (
          <div className="space-y-4">
            {actionMessage ? (
              <div className="rounded-2xl border border-emerald-500/25 bg-emerald-500/10 px-4 py-3 text-sm font-medium text-emerald-700 dark:text-emerald-300">
                {actionMessage}
              </div>
            ) : null}
            <div className="rounded-[24px] border border-border bg-[var(--color-surface)] p-4 shadow-sm">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="rounded-full bg-[var(--color-primary)]/10 px-2.5 py-1 text-xs font-semibold text-[var(--color-primary)]">
                      v{selectedVersion.versionNumber}
                    </span>
                    {selectedVersion.id === currentVersionId ? (
                      <span className="rounded-full border border-emerald-500/25 bg-emerald-500/10 px-2.5 py-1 text-xs font-medium text-emerald-700 dark:text-emerald-300">
                        Current live draft
                      </span>
                    ) : null}
                    {selectedVersion.id === publishedVersionId ? (
                      <span className="inline-flex items-center gap-1 rounded-full border border-amber-400/35 bg-amber-400/10 px-2.5 py-1 text-xs font-medium text-amber-700 dark:text-amber-300">
                        <Star className="h-3 w-3 fill-amber-400" />
                        Published live
                      </span>
                    ) : null}
                    {selectedVersion.id === lastKnownGoodVersionId ? (
                      <span className="rounded-full border border-amber-400/35 bg-amber-400/10 px-2.5 py-1 text-xs font-medium text-amber-700 dark:text-amber-300">
                        Last-known-good
                      </span>
                    ) : null}
                  </div>
                  <h3 className="mt-3 text-sm font-semibold text-foreground">
                    {readLabel(selectedVersion)}
                  </h3>
                </div>
                <Clock3 className="mt-0.5 h-4 w-4 text-muted-foreground" />
              </div>

              <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <div className="rounded-2xl border border-border bg-card px-3 py-2.5">
                  <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">Saved</div>
                  <div className="mt-1 font-medium text-foreground">{formatTimestamp(selectedVersion.createdAt)}</div>
                  <div className="mt-0.5 text-xs text-muted-foreground">{formatRelativeAge(selectedVersion.createdAt)}</div>
                </div>
                <div className="rounded-2xl border border-border bg-card px-3 py-2.5">
                  <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">Content</div>
                  <div className="mt-1 font-medium text-foreground">{countBlocks(selectedVersion.puckData)} blocks</div>
                  <div className="mt-0.5 text-xs text-muted-foreground">{selectedVersion.generationSource} source</div>
                </div>
              </div>

              <div className="mt-3 flex items-center gap-2 rounded-2xl border border-border bg-card px-3 py-2.5 text-sm text-muted-foreground">
                <User2 className="h-4 w-4" />
                <span className="truncate">{selectedVersion.createdBy}</span>
              </div>

              {selectedVersion.aiPrompt ? (
                <div className="mt-3 rounded-2xl border border-[var(--color-primary)]/15 bg-[var(--color-primary)]/5 px-3 py-3 text-sm text-foreground">
                  <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-[var(--color-primary)]">
                    <Sparkles className="h-3.5 w-3.5" />
                    AI prompt
                  </div>
                  <p className="mt-2 leading-6 text-muted-foreground">{selectedVersion.aiPrompt}</p>
                </div>
              ) : null}

              <div className="mt-4 flex flex-wrap gap-2">
                {onPreview ? (
                  <button
                    type="button"
                    onClick={() => onPreview(selectedVersion)}
                    className="inline-flex items-center gap-2 rounded-xl border border-border bg-card px-3 py-2 text-sm font-medium text-foreground transition hover:bg-accent"
                  >
                    <Eye className="h-4 w-4" />
                    Preview selected
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => handleRestore(selectedVersion)}
                  disabled={restoringId !== null || publishingRestoreId !== null}
                  className="inline-flex items-center gap-2 rounded-xl bg-[var(--gradient-energy)] px-3 py-2 text-sm font-medium text-white transition disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <RotateCcw className="h-4 w-4" />
                  {restoringId === selectedVersion.id ? "Restoring..." : "Restore as latest"}
                </button>
                <button
                  type="button"
                  onClick={() => handleRestoreAndPublish(selectedVersion)}
                  disabled={restoringId !== null || publishingRestoreId !== null}
                  className="inline-flex items-center gap-2 rounded-xl border border-amber-400/35 bg-amber-400/10 px-3 py-2 text-sm font-medium text-amber-800 transition hover:bg-amber-400/15 disabled:cursor-not-allowed disabled:opacity-60 dark:text-amber-200"
                >
                  <Star className="h-4 w-4" />
                  {publishingRestoreId === selectedVersion.id ? "Restoring & publishing..." : "Restore & Publish"}
                </button>
                <button
                  type="button"
                  onClick={() => toggleCompareSelection(selectedVersion.id)}
                  className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-medium transition ${
                    compareSelection.includes(selectedVersion.id)
                      ? "border-[var(--color-primary)]/35 bg-[var(--color-primary)]/10 text-[var(--color-primary)]"
                      : "border-border bg-card text-foreground hover:bg-accent"
                  }`}
                >
                  <GitCompareArrows className="h-4 w-4" />
                  {compareSelection.includes(selectedVersion.id) ? "Selected for compare" : "Compare this version"}
                </button>
              </div>
            </div>

            {comparison ? (
              <div className="rounded-[24px] border border-border bg-card p-4 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                      <GitCompareArrows className="h-4 w-4" />
                      Compare v{comparison.versionA.versionNumber} vs v{comparison.versionB.versionNumber}
                    </div>
                    <p className="mt-1 text-xs leading-5 text-muted-foreground">
                      Shared `compareVersions()` diff is active across the two selected checkpoints.
                    </p>
                  </div>
                  <div className="flex items-center gap-1 rounded-xl border border-border bg-[var(--color-surface)] p-1">
                    <button
                      type="button"
                      onClick={() => setDiffView("visual")}
                      className={`rounded-lg px-2.5 py-1 text-xs font-medium transition ${diffView === "visual" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:bg-accent"}`}
                    >
                      Visual
                    </button>
                    <button
                      type="button"
                      onClick={() => setDiffView("data")}
                      className={`rounded-lg px-2.5 py-1 text-xs font-medium transition ${diffView === "data" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:bg-accent"}`}
                    >
                      Data
                    </button>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-4 gap-3 text-sm">
                  <div className="rounded-2xl border border-border bg-[var(--color-surface)] px-3 py-2.5">
                    <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">Changed</div>
                    <div className="mt-1 font-semibold text-foreground">{comparison.changed.length}</div>
                  </div>
                  <div className="rounded-2xl border border-border bg-[var(--color-surface)] px-3 py-2.5">
                    <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">Added</div>
                    <div className="mt-1 font-semibold text-foreground">{comparison.onlyInB.length}</div>
                  </div>
                  <div className="rounded-2xl border border-border bg-[var(--color-surface)] px-3 py-2.5">
                    <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">Removed</div>
                    <div className="mt-1 font-semibold text-foreground">{comparison.onlyInA.length}</div>
                  </div>
                  <div className="rounded-2xl border border-border bg-[var(--color-surface)] px-3 py-2.5">
                    <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">Unchanged</div>
                    <div className="mt-1 font-semibold text-foreground">{comparison.unchanged}</div>
                  </div>
                </div>

                {diffView === "visual" ? (
                  <div className="mt-4 grid gap-4 lg:grid-cols-2">
                    {[
                      { label: `v${comparison.versionA.versionNumber}`, components: comparison.versionA.state.content, ownOnly: onlyInAIds },
                      { label: `v${comparison.versionB.versionNumber}`, components: comparison.versionB.state.content, ownOnly: onlyInBIds },
                    ].map((column) => (
                      <div key={column.label} className="rounded-2xl border border-border bg-[var(--color-surface)] p-3">
                        <div className="mb-3 text-sm font-semibold text-foreground">{column.label}</div>
                        <div className="space-y-2">
                          {column.components.map((component) => {
                            const componentId = component._id ?? component.type;
                            const isChanged = changedIds.has(componentId);
                            const isOwnOnly = column.ownOnly.has(componentId);

                            return (
                              <div
                                key={`${column.label}-${componentId}`}
                                className={`rounded-2xl border px-3 py-2.5 text-sm ${
                                  isChanged
                                    ? "border-amber-400/45 bg-amber-400/10"
                                    : isOwnOnly
                                      ? column.label === `v${comparison.versionA.versionNumber}`
                                        ? "border-rose-400/40 bg-rose-400/10"
                                        : "border-emerald-400/40 bg-emerald-400/10"
                                      : "border-border bg-card"
                                }`}
                              >
                                <div className="flex items-center justify-between gap-3">
                                  <span className="font-medium text-foreground">{component.type}</span>
                                  <span className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                                    {isChanged ? "changed" : isOwnOnly ? (column.label === `v${comparison.versionA.versionNumber}` ? "removed" : "added") : "same"}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="mt-4 space-y-3">
                    {comparison.changed.map((entry) => (
                      <div key={entry.componentId} className="rounded-2xl border border-amber-400/40 bg-amber-400/10 px-3 py-3">
                        <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                          <Braces className="h-4 w-4" />
                          {entry.type}
                        </div>
                        <div className="mt-2 text-xs text-muted-foreground">
                          Changed keys: {listChangedPropKeys(entry.propsA, entry.propsB).join(", ") || "props updated"}
                        </div>
                      </div>
                    ))}
                    {comparison.onlyInB.map((entry) => (
                      <div key={`added-${entry._id ?? entry.type}`} className="rounded-2xl border border-emerald-400/40 bg-emerald-400/10 px-3 py-3 text-sm text-foreground">
                        Added in v{comparison.versionB.versionNumber}: {entry.type}
                      </div>
                    ))}
                    {comparison.onlyInA.map((entry) => (
                      <div key={`removed-${entry._id ?? entry.type}`} className="rounded-2xl border border-rose-400/40 bg-rose-400/10 px-3 py-3 text-sm text-foreground">
                        Removed after v{comparison.versionA.versionNumber}: {entry.type}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : null}

            <div className="space-y-2" role="list" aria-label="Version ledger">
              {displayedVersions.map((version) => {
                const isSelected = version.id === selectedVersion.id;
                const isCurrent = version.id === currentVersionId;
                const isPublished = version.id === publishedVersionId;
                const isLastKnownGood = version.id === lastKnownGoodVersionId;

                return (
                  <div
                    key={version.id}
                    role="listitem"
                    className={`stagger-item flex items-center gap-3 rounded-2xl border px-3 py-3 transition ${
                      isSelected
                        ? "border-[var(--color-primary)]/35 bg-[var(--color-primary)]/5"
                        : "border-border bg-card"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => setSelectedVersionId(version.id)}
                      className="flex min-w-0 flex-1 items-center justify-between gap-3 text-left"
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-foreground">v{version.versionNumber}</span>
                          {isCurrent ? (
                            <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.14em] text-emerald-700 dark:text-emerald-300">
                              current
                            </span>
                          ) : null}
                          {isPublished ? (
                            <span className="rounded-full bg-amber-400/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.14em] text-amber-700 dark:text-amber-300">
                              published
                            </span>
                          ) : null}
                          {isLastKnownGood ? (
                            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.14em] text-amber-800">
                              last-known-good
                            </span>
                          ) : null}
                        </div>
                        <div className="mt-1 text-xs text-muted-foreground">{readLabel(version)}</div>
                      </div>
                      <div className="shrink-0 text-right text-xs text-muted-foreground">
                        <div>{formatRelativeAge(version.createdAt)}</div>
                        <div className="mt-1">{countBlocks(version.puckData)} blocks</div>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleCompareSelection(version.id)}
                      className={`shrink-0 rounded-xl border px-2.5 py-1.5 text-xs font-medium transition ${
                        compareSelection.includes(version.id)
                          ? "border-[var(--color-primary)]/35 bg-[var(--color-primary)]/10 text-[var(--color-primary)]"
                          : "border-border bg-[var(--color-surface)] text-muted-foreground hover:bg-accent"
                      }`}
                    >
                      Compare
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
